import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_R2, MASSAGER_LABEL_A2, MASSAGER_LABEL_R2 } from '@/constants/serviceType';

interface TopBarProps {
  breadcrumbItems?: Array<{
    label: string;
    href?: string;
  }>;
  showManualLink?: boolean;
  showSupportLink?: boolean;
  manualUrl?: string;
  supportUrl?: string;
  sidebarOpen?: boolean; // When true, adds left padding to align with main content
}

/**
 * Top Bar Component
 *
 * Displays below the main header with:
 * - Page breadcrumb/title on the left
 * - Manual and Support links on the right
 */
export const TopBar: React.FC<TopBarProps> = ({
  breadcrumbItems,
  showManualLink = true,
  showSupportLink = true,
  manualUrl,
  supportUrl = 'https://113366.com/rece',
  sidebarOpen = false,
}) => {
  const { colors, serviceType } = useTheme();
  const location = useLocation();

  // Check if we're on tenant home page
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isTenantHome = pathSegments.length <= 1;

  // Generate breadcrumb from URL path if not provided
  const displayBreadcrumb = useMemo(() => {
    if (breadcrumbItems) {
      return breadcrumbItems.map(item => item.label).join(' > ');
    }

    // Generate from URL pattern
    const path = location.pathname;

    // Service-specific prefixes
    const receproPrefix = serviceType === SERVICE_TYPE_R2 ? 'recepro R2 ' : 'recepro A2 ';
    const dailyWorkPrefix = `${receproPrefix}日次業務`;
    const monthlyWorkPrefix = `${receproPrefix}月次業務`;
    const masterPrefix = receproPrefix.trim(); // "recepro R" / "recepro A" for マスタメンテナンス

    // BD: …/patients/:id/histories (not …/patients/histories) — must run before generic `/patients` match
    if (/\/patients\/[^/]+\/histories(?:\/|$|\?)/.test(path)) {
      return `${dailyWorkPrefix} > 受診者履歴`;
    }

    // BD SCR-107: 施術報告書一覧 screen breadcrumb
    if (/\/treatment-reports\/[^/]+\/list(?:\/|$|\?)/.test(path)) {
      return `${dailyWorkPrefix} > 施術報告書一覧`;
    }

    // 患者リスト(施術師毎) — practitioner label branches on serviceType.
    // Must run before the generic `/visits` map entry below.
    if (/\/visits\/visitpatient_init(?:\/|$|\?)/.test(path)) {
      const practitionerLabel =
        serviceType === SERVICE_TYPE_R2 ? MASSAGER_LABEL_R2 : MASSAGER_LABEL_A2;
      return `${monthlyWorkPrefix} > 患者リスト(${practitionerLabel}毎)検索`;
    }

    // 訪問予定表(施術師毎) — A2/R2 use different 施術師 labels
    if (path.includes('/visits/massager_init')) {
      const massagerLabel = serviceType === SERVICE_TYPE_R2 ? MASSAGER_LABEL_R2 : MASSAGER_LABEL_A2;
      return `${monthlyWorkPrefix} > 予定表(${massagerLabel}毎)検索`;
    }

    const pathMap: Record<string, string> = {
      // RA2: レセプト用紙発注 (no 日次/月次; still gets recepro prefix)
      '/sheets/order': `${receproPrefix}レセプト用紙発注 > 印刷用紙発注`,
      // 日次・マスタ等
      '/patients/search_patients': `${monthlyWorkPrefix} > 患者情報検索`,
      '/patients/edit': `${dailyWorkPrefix} > 保険証入力`,
      '/patients/edit/': `${dailyWorkPrefix} > 保険証入力`,
      '/patients/copy': `${dailyWorkPrefix} > 保険証入力`,
      '/patients/histories': `${dailyWorkPrefix} > 受診者履歴`,
      '/patients/continuation': `${dailyWorkPrefix} > 継続受診者登録`,
      '/patients/certification': `${dailyWorkPrefix} > 保険証読取`,
      '/consents': `${dailyWorkPrefix} > 同意書入力`,
      '/consents/treatment_period': `${monthlyWorkPrefix} > 加療期間期限切れリスト`,
      '/long_term_frequents': `${dailyWorkPrefix} > 16回一覧`,
      '/treatment-reports': `${dailyWorkPrefix} > 施術報告書`,
      '/rezepts': `${monthlyWorkPrefix} > レセプト発行`,
      // Popup window opened from レセプト発行 — show only its own page name (matches legacy A2/R2).
      '/rezepts/visit_check_ready': `${receproPrefix}訪問データチェック結果`,
      '/rezepts/return_init': `${monthlyWorkPrefix} > 返戻処理`,
      '/rezepts/visit_clinic_invoices_init': `${dailyWorkPrefix} > [来院用]領収書(窓口)`,
      '/rezepts/invoices_init': `${dailyWorkPrefix} > [訪問用]領収書・請求書`,
      '/treatment_records/init': `${monthlyWorkPrefix} > 施術録出力`,
      '/rezept_histories/init': `${monthlyWorkPrefix} > レセプト内容CSV出力`,
      '/rezepts/init_massagers': `${monthlyWorkPrefix} > 施術師毎CSV出力`,
      '/visits/patient_init': `${monthlyWorkPrefix} > 予定表(患者毎)検索`,
      '/visits/massager_init':
        serviceType === SERVICE_TYPE_R2
          ? `${monthlyWorkPrefix} > 予定表(マッサージ師毎)検索`
          : `${monthlyWorkPrefix} > 予定表(施術師毎)検索`,
      '/visits/visitpatient_init':
        serviceType === SERVICE_TYPE_R2
          ? `${monthlyWorkPrefix} > 患者リスト(マッサージ師毎)検索`
          : `${monthlyWorkPrefix} > 患者リスト(施術師毎)検索`,
      '/visits': `${dailyWorkPrefix} > 訪問管理`,
      '/visits/schedule': `${dailyWorkPrefix} > 訪問入力`,
      '/visit_clinic/': `${dailyWorkPrefix} > 施術入力`,
      '/clinic_informations/init': `${masterPrefix} マスタメンテナンス > 治療院基本情報`,
      '/insurers/init': `${masterPrefix} マスタメンテナンス > 保険者マスタ`,
      '/insurer_attentions/init': `${masterPrefix} マスタメンテナンス > 保険者注意事項`,
      '/massagers/init': `${masterPrefix} マスタメンテナンス > はり・きゅう師`,
      '/hospitals/init': `${masterPrefix} マスタメンテナンス > 医療機関`,
      '/document_templates/init': `${masterPrefix} マスタメンテナンス > 文書テンプレート`,
      '/clinic_locations': `${masterPrefix} マスタメンテナンス > 拠点管理`,
      '/free_medical/init': `${masterPrefix} マスタメンテナンス > 自由診療メニュー`,
      '/medical_expense_prices/init': `${masterPrefix} マスタメンテナンス > 健康保険料金`,
      '/sheets/histories': `${receproPrefix}レセプト用紙発注 > 発注履歴`,
      '/contact/edit': `${receproPrefix}サポート > お問い合わせ`,
      '/contact/complete':
        serviceType === SERVICE_TYPE_R2
          ? `${receproPrefix}マッサージ師向けレセプト発行`
          : `${receproPrefix}はり・きゅう師向けレセプト発行`,
    };

    if (path.includes('/patients/') && path.includes('/dminfo/edit')) {
      return `${dailyWorkPrefix} > DM情報`;
    }

    // Match longer path first (RA2: specific path wins)
    const sortedEntries = Object.entries(pathMap).sort((a, b) => b[0].length - a[0].length);
    for (const [pattern, label] of sortedEntries) {
      if (path.includes(pattern)) {
        return label;
      }
    }

    return 'ホーム';
  }, [breadcrumbItems, location, serviceType]);

  // Determine manual URL based on current page (default: app manual PDF)
  const currentManualUrl = useMemo(() => {
    //   const path = location.pathname;
    //   const cleanPath = path
    //     .replace(/\/[0-9-]+/g, '') // Remove IDs
    //     .replace(/\?[\S|\s]+/g, ''); // Remove query params

    //   // Construct path to manual directory based on route
    //   if (cleanPath.includes('patients/edit')) {
    //     return '/storage/manual/patients/edit/';
    //   }
    //   if (cleanPath.includes('patients/histories')) {
    //     return '/storage/manual/patients/histories/';
    //   }
    //   if (cleanPath.includes('patients/dminfo/edit')) {
    //     return '/storage/manual/patients/dminfo/edit/';
    //   }
    //   if (cleanPath.includes('clinic_informations/init')) {
    //     return '/storage/manual/clinic_informations/init/';
    //   }
    //   if (cleanPath.includes('insurers/init')) {
    //     return '/storage/manual/insurers/init/';
    //   }
    //   if (cleanPath.includes('massagers/init')) {
    //     return '/storage/manual/massagers/init/';
    //   }
    //   if (cleanPath.includes('hospitals/init')) {
    //     return '/storage/manual/hospitals/init/';
    //   }
    //   if (cleanPath.includes('document_templates/init')) {
    //     return '/storage/manual/document_templates/init/';
    //   }
    //   if (cleanPath.includes('sheets/order')) {
    //     return '/storage/manual/sheets/order/';
    //   }

    //   return `/storage/manual${cleanPath}/`;
    // }, [manualUrl, location]);
    if (manualUrl) return manualUrl;
    return '/example.pdf';
  }, [manualUrl]);

  return (
    <div
      className="px-4 py-2 border-b border-gray-200 shadow-sm top-16 z-60"
      style={{
        backgroundColor: colors.primaryDark,
        transition: 'padding-left 0.3s ease',
      }}
    >
      <div
        className={`flex justify-between items-center w-full transition-all duration-300 ${
          sidebarOpen ? 'lg:pl-60' : ''
        }`}
      >
        {/* Left: Breadcrumb */}
        <div className="flex-1 hidden text-sm font-medium text-white/90 md:block">
          {displayBreadcrumb}
        </div>

        {/* Right: Action Links */}
        <div className="flex items-center gap-6 ml-auto">
          {/* Manual Link */}
          {showManualLink && !isTenantHome && (
            <a
              href={currentManualUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-white transition-opacity hover:opacity-80 whitespace-nowrap"
              title="マニュアルを開く"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
              <span className="hidden sm:inline">マニュアル</span>
            </a>
          )}

          {/* Support Link */}
          {showSupportLink && (
            <a
              href={supportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-white transition-opacity hover:opacity-80 whitespace-nowrap"
              title="サポートを開く"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20,2H4A2,2 0 0,0 2,4V22L6,18H20A2,2 0 0,0 22,16V4A2,2 0 0,0 20,2Z" />
              </svg>
              <span className="hidden sm:inline">サポート</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopBar;
